/**
 * The setup check the loader answers before it reads a session (layered shell surfaces, D18,
 * WS-6 §3.7).
 */

/** `setup` while the org's first-run wizard is open, else `ready`. */
export type SetupGateVerdict = 'setup' | 'ready';

/** The setup-status route core-api serves to anyone. */
export const SETUP_STATUS_URL = '/v1/setup/status';

/** How long the status request may take before the loader proceeds as `ready`. */
export const SETUP_STATUS_TIMEOUT_MS = 10_000;

/**
 * Ask core-api whether setup is open.
 *
 * Sends one `GET` {@link SETUP_STATUS_URL} with a {@link SETUP_STATUS_TIMEOUT_MS} timeout. A 2xx
 * body `{ mode: 'setup' }` answers `setup`. Every other answer answers `ready`: a non-2xx status,
 * a network error, a timeout, a body that is not JSON, and an unknown `mode`. This keeps the old
 * `SetupModeProvider` rule that a broken status route never locks a configured org out. Never
 * retries; never rejects.
 */
export async function readSetupMode(fetcher: typeof fetch): Promise<SetupGateVerdict> {
  try {
    const res = await fetcher(SETUP_STATUS_URL, { signal: AbortSignal.timeout(SETUP_STATUS_TIMEOUT_MS) });
    if (!res.ok) return 'ready';
    const body: unknown = JSON.parse(await res.text());
    return typeof body === 'object' && body !== null && Reflect.get(body, 'mode') === 'setup' ? 'setup' : 'ready';
  } catch {
    return 'ready';
  }
}
